/**
 * Backup filename builder (client) — mirrors server/lib/backupNaming.js so the live
 * preview in the Download panel matches the actual downloaded names.
 */
export type TokenId = 'date' | 'owner' | 'folder' | 'pay' | 'amount' | 'party' | 'type' | 'ref';

export const TOKEN_LABELS: Record<TokenId, string> = {
  date: 'Date', owner: 'Folder Owner Nickname', folder: 'Folder Name', pay: 'Payment Method',
  amount: 'Amount', party: 'Party', type: 'Document Type', ref: 'Reference #',
};
export const ALL_TOKENS: TokenId[] = ['date', 'owner', 'folder', 'pay', 'amount', 'party', 'type', 'ref'];

export interface Preset { id: string; label: string; order: TokenId[] | 'original'; }
export const PRESETS: Preset[] = [
  { id: 'original', label: 'Original name', order: 'original' },
  { id: 'dofpa', label: 'Date _ Folder Owner Nickname _ Folder Name _ Payment Method _ Amount', order: ['date', 'owner', 'folder', 'pay', 'amount'] },
  { id: 'fdpa',  label: 'Folder Name _ Date _ Payment Method _ Amount',                          order: ['folder', 'date', 'pay', 'amount'] },
  { id: 'odfpa', label: 'Folder Owner Nickname _ Date _ Folder Name _ Payment Method _ Amount',  order: ['owner', 'date', 'folder', 'pay', 'amount'] },
  { id: 'custom', label: 'Custom order…', order: ['date', 'owner', 'folder', 'pay', 'amount'] },
];

export interface NameDoc {
  document_id: number;
  date_of_document?: string | null;
  created_at?: string | null;
  upload_date?: string | null;
  payment_method?: string | null;
  total_amount?: number | string | null;
  party_name?: string | null;
  invoice_ref?: string | null;
  filename?: string | null;
  original_filename?: string | null;
}
export interface NameCtx { ownerNick?: string; folderName?: string; typeName?: string; }

export function sanitizeSeg(s: any): string {
  const cleaned = String(s == null ? '' : s)
    .replace(/[\\/:*?"<>|]/g, ' ')
    .split('')
    .filter(ch => ch.charCodeAt(0) >= 32)
    .join('');
  return cleaned.replace(/\s+/g, ' ').replace(/^\.+|\.+$/g, '').trim();
}

function ensurePdf(name: string): string {
  return /\.pdf$/i.test(name) ? name : name + '.pdf';
}

function toYmd(v: any): string {
  if (!v) return '';
  if (v instanceof Date) return isNaN(v.getTime()) ? '' : v.toISOString().slice(0, 10);
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  return isNaN(d.getTime()) ? s.slice(0, 10) : d.toISOString().slice(0, 10);
}

function tokenValue(id: TokenId, doc: NameDoc, ctx: NameCtx): string {
  switch (id) {
    case 'date':
      return toYmd(doc.date_of_document || doc.created_at || doc.upload_date || '');
    case 'owner':  return ctx.ownerNick || '';
    case 'folder': return ctx.folderName || '';
    case 'pay':    return doc.payment_method || '';
    case 'amount': return (doc.total_amount != null && doc.total_amount !== '')
      ? Number(doc.total_amount).toFixed(2) : '';
    case 'party':  return doc.party_name || '';
    case 'type':   return ctx.typeName || '';
    case 'ref':    return doc.invoice_ref || '';
    default:       return '';
  }
}

export function buildName(order: TokenId[] | 'original', sep: string, doc: NameDoc, ctx: NameCtx = {}): string {
  if (order === 'original') {
    const raw = doc.filename || doc.original_filename || ('document_' + doc.document_id);
    const base = sanitizeSeg(raw.replace(/\.pdf$/i, ''));
    return ensurePdf(base || ('document_' + doc.document_id));
  }
  const s = sep || '_';
  const parts = order.map(id => sanitizeSeg(tokenValue(id, doc, ctx))).filter(Boolean);
  let base = parts.join(s);
  if (!base) base = 'document_' + doc.document_id;
  base = base.slice(0, 200);
  return ensurePdf(base);
}
